import { Fetcher } from '@/api/fetcher'
import { SupplierStripeData } from '@/utils/admin/get-supplier-stripe-data'
import { ListingWithCategoryAndSubscription } from '@/app/api/admin/suppliers/listings/route'
import { SupplierWithArticlesFlat } from '@/app/api/admin/suppliers/report/articles/route'
import { SupplierReportRow } from '@/app/api/admin/suppliers/report/route'
import { CustomerEvent } from '@/app/api/admin/users/customer-events/route'
import { API_URL } from '@/constants'
import { ApiError } from '@/errors/apiError'
import { ArticleCategory } from '@/types/api/article-category'
import { PaginationResponse } from '@/types/api/pagination-response'
import { SupplierApplication } from '@/types/api/supplier-application'
import { SupplierStats } from '@/types/api/supplier-stats'
import { Image } from '@/types/Image'
import {
  ApiAdminSupplier,
  SupplierApplicationWithAccount,
} from '@/types/Supplier'
import { SupplierListingStatus, UserStatus } from '@prisma/client'
import { RequestInit } from 'next/dist/server/web/spec-extension/request'
import { ApiHandlerResult } from '..'
import { BaseApi } from '../base'

export type GetImagesOptionsType = {
  limit?: number
  page?: number
  search?: string
  type?: 'article' | 'recycle' | 'supplier'
}

type UpdateImagePayloadType = {
  id: number
  alt: string
}

type GetSuppliersListingsParams = {
  pageSize?: number
  page?: number
  search?: string
  status?: SupplierListingStatus
  category?: number
  sortOrder?: 'asc' | 'desc'
  sortField?: string
}

export type GetCustomerEventsParams = {
  pageSize?: number
  page?: number
  status?: UserStatus
  search?: string
}

export class AdminApi extends BaseApi {
  constructor(fetcher: Fetcher) {
    super(fetcher)
  }

  async getSupplierApplication(
    id: number | string,
    options?: RequestInit
  ): ApiHandlerResult<SupplierApplication> {
    try {
      const res = await this.fetcher.get(
        `${API_URL.ADMIN.SUPPLIER_APPLICATION}/${id}`,
        { ...options }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getReviewers(
    options?: RequestInit
  ): ApiHandlerResult<ArticleCategory[]> {
    try {
      const res = await this.fetcher.get(
        `${API_URL.ADMIN.REVIEWERS}?no_limit=true`, // not sure what no_limit does
        { ...options }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getAdminById(
    id: number | string,
    options?: RequestInit
  ): ApiHandlerResult<ArticleCategory[]> {
    try {
      const res = await this.fetcher.get(`${API_URL.ADMIN.REVIEWERS}/${id}`, {
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getSupplierById(
    id: string | number,
    options?: RequestInit
  ): ApiHandlerResult<ApiAdminSupplier> {
    try {
      const res = await this.fetcher.get(`${API_URL.ADMIN.SUPPLIER}/${id}`, {
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getImages(
    params?: GetImagesOptionsType,
    options?: RequestInit
  ): ApiHandlerResult<PaginationResponse<Image>> {
    try {
      const res = await this.fetcher.get(API_URL.ADMIN.IMAGES, {
        params,
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getSupplierApplicationWithAccount(
    params: {
      page?: number
      statuses?: string | string[]
      search?: string
      business_categories?: string | string[]
    },
    options?: RequestInit
  ): ApiHandlerResult<PaginationResponse<SupplierApplicationWithAccount>> {
    try {
      const res = await this.fetcher.get(API_URL.ADMIN.SUPPLIER_APPLICATION, {
        params,
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async postSupplierInvite(
    body: {
      email: string
      business_name: string
      // The backend looks the category up by its Int id; a string is rejected.
      business_category: number
      message: string
    },
    options?: RequestInit
  ): ApiHandlerResult<any> {
    try {
      const res = await this.fetcher.post(API_URL.ADMIN.SUPPLIER_INVITE, {
        body: JSON.stringify(body),
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getCreateSupplierLink(
    id: number,
    options?: RequestInit
  ): ApiHandlerResult<{ url: string }> {
    try {
      const res = await this.fetcher.get(
        `${API_URL.ADMIN.CREATE_SUPPLIER_LINK}/${id}`,
        {
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async updateImage(
    payload: UpdateImagePayloadType,
    options?: RequestInit
  ): ApiHandlerResult<null> {
    try {
      const { id, ...restImage } = payload
      const res = await this.fetcher.put(`${API_URL.ADMIN.IMAGE}/${id}`, {
        body: JSON.stringify(restImage),
        ...options,
      })

      if (res.status !== 204) {
        const errBody = await res.json()

        return [new ApiError(res.status, errBody), null]
      }

      return [null, null]
    } catch (err: any) {
      return [err, null]
    }
  }

  async getSuppliersStats(
    params: { suppliersIds?: number[]; startDate?: string; endDate?: string },
    options?: RequestInit
  ): ApiHandlerResult<SupplierStats[]> {
    try {
      const res = await this.fetcher.get(API_URL.ADMIN.SUPPLIERS_STATS, {
        params,
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async putSupplier(
    id: number,
    data: any,
    options?: RequestInit
  ): ApiHandlerResult<any> {
    try {
      const res = await this.fetcher.put(`${API_URL.ADMIN.SUPPLIER}/${id}`, {
        body: JSON.stringify(data),
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getSupplierReport(): ApiHandlerResult<SupplierReportRow[]> {
    try {
      const res = await this.fetcher.get(API_URL.ADMIN.SUPPLIERS_REPORT)

      if (res.status !== 200) {
        const errBody = await res.json()
        console.log('>> error api.admin.getSupplierReport', errBody)
        return [new ApiError(res.status, errBody), null]
      }

      const body = await res.json()
      return [null, body]
    } catch (err: any) {
      return [err, null]
    }
  }

  async getSupplierArticlesReport(): ApiHandlerResult<
    SupplierWithArticlesFlat[]
  > {
    try {
      const res = await this.fetcher.get(
        API_URL.ADMIN.SUPPLIERS_ARTICLES_REPORT
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getSuppliersListings(
    params: GetSuppliersListingsParams,
    options?: RequestInit
  ): ApiHandlerResult<PaginationResponse<ListingWithCategoryAndSubscription>> {
    try {
      const res = await this.fetcher.get(API_URL.ADMIN.SUPPLIERS.LISTINGS, {
        params,
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  /**
   * @description Use only via feTofe
   */
  async disableUser(
    userId: number,
    options?: RequestInit
  ): ApiHandlerResult<null> {
    try {
      const res = await this.fetcher.post(
        `${API_URL.ADMIN.USERS.ROOT}/${userId}/disable`,
        {
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async gdprDeleteUser(
    userId: number,
    options?: RequestInit
  ): ApiHandlerResult<null> {
    try {
      const res = await this.fetcher.delete(
        `${API_URL.ADMIN.USERS.ROOT}/${userId}/gdpr-delete`,
        {
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getCustomerEvents(
    params: GetCustomerEventsParams,
    options?: RequestInit
  ): ApiHandlerResult<PaginationResponse<CustomerEvent>> {
    try {
      const res = await this.fetcher.get(API_URL.ADMIN.USERS.CUSTOMER_EVENTS, {
        params,
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      console.log(err)
      return [err, null]
    }
  }

  async getSupplierStripeData(
    supplierId: number | string,
    options?: RequestInit
  ): ApiHandlerResult<SupplierStripeData> {
    try {
      const res = await this.fetcher.get(
        API_URL.ADMIN.SUPPLIERS.STRIPE(supplierId),
        { ...options }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async updateSupplierStripeSubscription(
    supplierId: number | string,
    subscriptionId: string,
    options?: RequestInit
  ): ApiHandlerResult<{ success: boolean }> {
    try {
      const res = await this.fetcher.put(
        API_URL.ADMIN.SUPPLIERS.STRIPE(supplierId),
        {
          body: JSON.stringify({ subscriptionId }),
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async updateUserStripeCustomerId(
    userId: number | string,
    customerId: string,
    options?: RequestInit
  ): ApiHandlerResult<{ success: boolean }> {
    try {
      const res = await this.fetcher.put(
        API_URL.ADMIN.USERS.STRIPE_CUSTOMER(userId),
        {
          body: JSON.stringify({ customerId }),
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }
}
